import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { setupRouterGuards } from './guards'
import { resolveConsoleSection } from '@/rbac/console-sections'

// U3 (§7.3.27): stamp `meta.section` onto every route from the single-source
// SECTION_ROUTE_TABLE so no protected Console page can be silently un-gated. The
// full path (parent + child) drives resolution; a record that already declares a
// section (e.g. an explicit override) is left untouched. Records whose full path
// does not resolve to a Console section (login, /403, /404, redirect shims) get
// `section: null` — the guard treats null as "not section-gated" and lets the
// coarse requiresAuth/roles checks stand. The U3 permission-matrix evidence
// script asserts every *named page* under the Console shell resolves to a
// non-null section, so a forgotten page fails the acceptance gate.
function stampSections(records: RouteRecordRaw[], parentPath = ''): void {
  for (const record of records) {
    const raw = record.path ?? ''
    const fullPath = raw.startsWith('/')
      ? raw
      : `${parentPath}/${raw}`.replace(/\/+/g, '/')
    const meta = (record.meta ?? (record.meta = {})) as Record<string, unknown>
    if (!('section' in meta)) {
      meta.section = resolveConsoleSection(fullPath)
    }
    if (record.children?.length) {
      stampSections(record.children as RouteRecordRaw[], fullPath === '/' ? '' : fullPath)
    }
  }
}

const routes: RouteRecordRaw[] = [
  // Public routes
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { title: 'Login' },
  },

  // Console routes (protected)
  {
    path: '/',
    component: () => import('@/pages/layouts/ConsoleLayout.vue'),
    meta: {
      requiresAuth: true,
      roles: ['site_admin', 'site_owner'],
    },
    children: [
      {
        path: '',
        redirect: { name: 'console-dashboard' },
      },

      // ═══════════════════════════════════════════════════════════════════════
      // 📊 Dashboard
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'dashboard',
        name: 'console-dashboard',
        component: () => import('@/pages/dashboard/DashboardPage.vue'),
        meta: { title: 'Dashboard' },
      },
      { path: 'admin/dashboard', redirect: '/dashboard' },

      // ═══════════════════════════════════════════════════════════════════════
      // 📈 Monitoring Hub — /monitoring/:tab
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'monitoring',
        component: () => import('@/pages/monitoring/MonitoringHubPage.vue'),
        meta: { title: 'Monitoring' },
        children: [
          { path: '', redirect: '/monitoring/system' },
          { path: 'system', name: 'monitoring-system', component: () => import('@/pages/monitoring/MonitoringDashboardPage.vue') },
          { path: 'alerts', name: 'monitoring-alerts', component: () => import('@/pages/monitoring/AlertReviewPage.vue') },
          { path: 'trading', name: 'monitoring-trading', component: () => import('@/pages/trading/TradingOverviewPage.vue') },
          { path: 'orders', name: 'monitoring-orders', component: () => import('@/pages/monitoring/OrderStatsPage.vue') },
          { path: 'signal', name: 'monitoring-signal', component: () => import('@/pages/monitoring/SignalPositionPage.vue') },
          { path: 'slip', name: 'monitoring-slip', component: () => import('@/pages/monitoring/SlipStatsPage.vue') },
          { path: 'subs-stats', name: 'monitoring-subs-stats', component: () => import('@/pages/monitoring/SubsStatsPage.vue') },
          { path: 'market-data', name: 'monitoring-market-data', component: () => import('@/pages/monitoring/MarketDataViewerPage.vue') },
        ],
      },
      // Performance / Audit / Controlled Ops split hubs
      {
        path: 'perf-audit',
        component: () => import('@/pages/monitoring/PerfAuditHubPage.vue'),
        meta: { title: 'PerfAudit' },
        children: [
          { path: '', redirect: '/perf-audit/performance' },
          { path: 'performance', name: 'monitoring-performance', component: () => import('@/pages/monitoring/PerformancePage.vue') },
          { path: 'audit', name: 'monitoring-audit', component: () => import('@/pages/monitoring/AuditReconciliationPage.vue') },
          { path: 'compare', name: 'monitoring-compare', component: () => import('@/pages/performance/PerformanceComparePage.vue') },
          { path: 'report', name: 'monitoring-report', component: () => import('@/pages/performance/StrategyReportPage.vue') },
          { path: 'dd', name: 'monitoring-dd', component: () => import('@/pages/admin/GlobalDDPreviewPage.vue') },
          { path: 'aum', name: 'monitoring-aum', component: () => import('@/pages/monitoring/WeightedAumPage.vue') },
          { path: 'certificates', name: 'monitoring-certificates', component: () => import('@/pages/monitoring/CertificatesPage.vue') },
        ],
      },
      {
        path: 'controlled-ops',
        component: () => import('@/pages/monitoring/ControlledOpsHubPage.vue'),
        meta: { title: 'ControlledOps' },
        children: [
          { path: '', redirect: '/controlled-ops/risk' },
          { path: 'risk', name: 'monitoring-risk', component: () => import('@/pages/monitoring/RiskControlTrialPage.vue') },
          { path: 'manual-orders', name: 'monitoring-manual-orders', component: () => import('@/pages/monitoring/ManualOrdersPage.vue') },
          { path: 'rehearsal', name: 'monitoring-rehearsal', component: () => import('@/pages/monitoring/RehearsalPreviewPage.vue') },
          { path: 'anonymization', name: 'monitoring-anonymization', component: () => import('@/pages/monitoring/AnonymizationReviewPage.vue') },
          { path: 'identity-cases', name: 'monitoring-identity-cases', component: () => import('@/pages/monitoring/IdentityCasesPage.vue') },
        ],
      },
      // Legacy redirects → Monitoring / split hubs
      { path: 'monitoring/dashboard', redirect: '/monitoring/system' },
      { path: 'monitoring/performance', redirect: '/perf-audit/performance' },
      { path: 'monitoring/compare', redirect: '/perf-audit/compare' },
      { path: 'monitoring/report', redirect: '/perf-audit/report' },
      { path: 'monitoring/dd', redirect: '/perf-audit/dd' },
      { path: 'monitoring/aum', redirect: '/perf-audit/aum' },
      { path: 'monitoring/certificates', redirect: '/perf-audit/certificates' },
      { path: 'monitoring/audit', redirect: '/perf-audit/audit' },
      { path: 'monitoring/risk', redirect: '/controlled-ops/risk' },
      { path: 'monitoring/manual-orders', redirect: '/controlled-ops/manual-orders' },
      { path: 'monitoring/rehearsal', redirect: '/controlled-ops/rehearsal' },
      { path: 'monitoring/anonymization', redirect: '/controlled-ops/anonymization' },
      { path: 'stranity', redirect: '/monitoring/trading' },
      { path: 'sikax', redirect: '/monitoring/trading' },
      { path: 'eltrade', redirect: '/monitoring/trading' },
      { path: 'monitoring/signal-position', redirect: '/monitoring/signal' },
      { path: 'monitoring/risk-control', redirect: '/controlled-ops/risk' },
      { path: 'admin/dd-preview', redirect: '/perf-audit/dd' },
      { path: 'monitoring/weighted-aum', redirect: '/perf-audit/aum' },
      { path: 'performance/compare', redirect: '/perf-audit/compare' },
      { path: 'performance/report', redirect: '/perf-audit/report' },

      // ═══════════════════════════════════════════════════════════════════════
      // 🏢 Operations Hub — /operations/:tab/:param?
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'operations',
        component: () => import('@/pages/operations/OperationsHubPage.vue'),
        meta: { title: 'Operations' },
        children: [
          { path: '', redirect: '/operations/users' },
          // Users
          { path: 'users', name: 'operations-users', component: () => import('@/pages/admin/UsersPage.vue') },
          { path: 'user-dashboard/:userId?', name: 'operations-user-dashboard', component: () => import('@/pages/admin/UserDashboardPage.vue') },
          { path: 'tickets', name: 'operations-tickets', component: () => import('@/pages/admin/TicketsPage.vue') },
          // Strategies
          { path: 'strategies', name: 'operations-strategies', component: () => import('@/pages/strategy/StrategiesHubPage.vue') },
          { path: 'strategy-dev', name: 'operations-strategy-dev', component: () => import('@/pages/strategy-dev/StrategyDevPage.vue') },
          { path: 'vendors', name: 'operations-vendors', component: () => import('@/pages/strategy/StrategyVendorsPage.vue') },
          // Business
          { path: 'resellers', name: 'operations-resellers', component: () => import('@/pages/crm/ResellersPage.vue') },
          { path: 'salesman', name: 'operations-salesman', component: () => import('@/pages/crm/SalesmanUserPage.vue') },
          { path: 'referral', name: 'operations-referral', component: () => import('@/pages/crm/UserReferralPage.vue') },
          { path: 'contracts', name: 'operations-contracts', component: () => import('@/pages/crm/ContractMgmtPage.vue') },
          { path: 'sip', name: 'operations-sip', component: () => import('@/pages/crm/RegularIncreasePage.vue') },
        ],
      },
      // Legacy redirects → Operations Hub (from old Users Hub)
      { path: 'users', redirect: '/operations/users' },
      { path: 'users/users', redirect: '/operations/users' },
      { path: 'users/dashboard/:userId?', redirect: to => `/operations/user-dashboard/${to.params.userId || ''}` },
      { path: 'users/tickets', redirect: '/operations/tickets' },
      { path: 'admin/users', redirect: '/operations/users' },
      { path: 'admin/user-dashboard/:userId?', redirect: to => `/operations/user-dashboard/${to.params.userId || ''}` },
      { path: 'admin/tickets', redirect: '/operations/tickets' },
      // Legacy redirects → Operations Hub (from old Trading Hub — strategy pages)
      { path: 'strategy/vendors', redirect: '/operations/vendors' },
      { path: 'strategy/list', redirect: '/operations/strategies' },
      { path: 'strategy/futures', redirect: '/operations/strategies' },
      { path: 'strategy/stocks', redirect: '/operations/strategies' },
      { path: 'strategy/options', redirect: '/operations/strategies' },
      { path: 'strategy-dev', redirect: '/operations/strategy-dev' },
      // Legacy redirects → Operations Hub (from old Business Hub)
      { path: 'business', redirect: '/operations/resellers' },
      { path: 'business/resellers', redirect: '/operations/resellers' },
      { path: 'business/referral', redirect: '/operations/referral' },
      { path: 'business/contracts', redirect: '/operations/contracts' },
      { path: 'business/sip', redirect: '/operations/sip' },
      { path: 'business/salesman', redirect: '/operations/salesman' },
      { path: 'crm/resellers', redirect: '/operations/resellers' },
      { path: 'admin/resellers', redirect: '/operations/resellers' },
      { path: 'crm/user-referral', redirect: '/operations/referral' },
      { path: 'crm/contracts', redirect: '/operations/contracts' },
      { path: 'crm/regular-increase', redirect: '/operations/sip' },
      { path: 'crm/salesman', redirect: '/operations/salesman' },

      // Old Trading Hub redirects → Monitoring or Operations
      { path: 'trading', redirect: '/monitoring/trading' },
      { path: 'trading/overview', redirect: '/monitoring/trading' },
      { path: 'trading/performance', redirect: '/perf-audit/performance' },
      { path: 'trading/orders', redirect: '/monitoring/orders' },
      { path: 'trading/subscriptions', redirect: '/finance/subscriptions' },
      { path: 'trading/slip', redirect: '/monitoring/slip' },
      { path: 'trading/strategies', redirect: '/operations/strategies' },
      { path: 'trading/compare', redirect: '/perf-audit/compare' },
      { path: 'trading/report', redirect: '/perf-audit/report' },
      { path: 'trading/dev', redirect: '/operations/strategy-dev' },
      { path: 'trading/vendors', redirect: '/operations/vendors' },
      { path: 'trading/signal', redirect: '/monitoring/signal' },
      { path: 'trading/risk', redirect: '/controlled-ops/risk' },
      { path: 'trading/dd', redirect: '/perf-audit/dd' },
      { path: 'trading/aum', redirect: '/perf-audit/aum' },

      // Old monitoring/subscriptions → finance
      { path: 'monitoring/subscriptions', redirect: '/finance/subscriptions' },

      // ═══════════════════════════════════════════════════════════════════════
      // 👥 我的客戶 Hub — /clients/:tab
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'clients',
        component: () => import('@/pages/clients/ClientsHubPage.vue'),
        meta: { title: '我的客戶' },
        children: [
          { path: '', redirect: '/clients/monitoring' },
          { path: 'monitoring', name: 'clients-monitoring', component: () => import('@/pages/clients/ClientsMonitoringPage.vue') },
          { path: 'list', name: 'clients-list', component: () => import('@/pages/admin/ClientsOverviewPage.vue') },
        ],
      },

      // ═══════════════════════════════════════════════════════════════════════
      // 💰 Finance Hub — /finance/:tab
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'finance',
        component: () => import('@/pages/finance/FinanceHubPage.vue'),
        meta: { title: 'Finance' },
        children: [
          { path: '', redirect: '/finance/overview' },
          { path: 'overview', name: 'finance-overview', component: () => import('@/pages/accounting/BillingOverviewPage.vue') },
          { path: 'reports', name: 'finance-reports', component: () => import('@/pages/accounting/AccountingPage.vue') },
          { path: 'subscriptions', name: 'finance-subscriptions', component: () => import('@/pages/accounting/SubscriptionsPage.vue') },
          { path: 'checkout', name: 'finance-checkout', component: () => import('@/pages/crm/UserCheckoutPage.vue') },
          { path: 'execution', name: 'finance-execution', component: () => import('@/pages/accounting/BillingExecutionPage.vue') },
          { path: 'recompute', name: 'finance-recompute', component: () => import('@/pages/admin/RecomputeRequestsPage.vue') },
          { path: 'cash', name: 'finance-cash', component: () => import('@/pages/accounting/CashManagementPage.vue') },
        ],
      },
      // Legacy redirects → Finance Hub
      { path: 'finance/audit', redirect: '/perf-audit/audit' },
      { path: 'accounting/billing', redirect: '/finance/overview' },
      { path: 'accounting', redirect: '/finance/reports' },
      { path: 'accounting/subscriptions', redirect: '/finance/subscriptions' },
      { path: 'crm/checkout', redirect: '/finance/checkout' },
      { path: 'accounting/billing-execution', redirect: '/finance/execution' },
      { path: 'monitoring/accounting', redirect: '/finance/overview' },
      { path: 'settings/recompute', redirect: '/finance/recompute' },
      { path: 'settings/cash', redirect: '/finance/cash' },
      { path: 'admin/recompute-requests', redirect: '/finance/recompute' },

      // ═══════════════════════════════════════════════════════════════════════
      // ⚙️ Settings Hub — /settings/:category/:tab?
      // ═══════════════════════════════════════════════════════════════════════
      {
        path: 'settings',
        component: () => import('@/pages/settings/SettingsHubPage.vue'),
        meta: { title: 'Settings' },
        children: [
          { path: '', redirect: '/settings/roles' },
          // 系統設定
          { path: 'roles', name: 'settings-roles', component: () => import('@/pages/admin/RolesPage.vue') },
          { path: 'groups', name: 'settings-groups', component: () => import('@/pages/sys/UserGroupsPage.vue') },
          { path: 'tenants', name: 'settings-tenants', component: () => import('@/pages/admin/TenantsPage.vue') },
          { path: 'erp', name: 'settings-erp', component: () => import('@/pages/admin/ErpCustomerPage.vue') },
          { path: 'holidays', name: 'settings-holidays', component: () => import('@/pages/sys/HolidaysPage.vue') },
          { path: 'email', name: 'settings-email', component: () => import('@/pages/admin/EmailConfigPage.vue') },
          { path: 'settings', name: 'settings-global', component: () => import('@/pages/admin/SettingsPage.vue') },
          { path: 'surveycake', name: 'settings-surveycake', component: () => import('@/pages/crm/SvcakeSettingsPage.vue') },
          { path: 'tokens', name: 'settings-tokens', component: () => import('@/pages/crm/LongLivedTokensPage.vue') },
          {
            path: 'roles-tenants',
            component: () => import('@/pages/settings/RolesTenantsHubPage.vue'),
            children: [
              { path: '', redirect: '/settings/roles-tenants/roles' },
              { path: 'roles', name: 'settings-roles-tenants-roles', component: () => import('@/pages/admin/RolesPage.vue') },
              { path: 'groups', name: 'settings-roles-tenants-groups', component: () => import('@/pages/sys/UserGroupsPage.vue') },
              { path: 'tenants', name: 'settings-roles-tenants-tenants', component: () => import('@/pages/admin/TenantsPage.vue') },
              { path: 'erp', name: 'settings-roles-tenants-erp', component: () => import('@/pages/admin/ErpCustomerPage.vue') },
              { path: 'platform-roles', name: 'settings-roles-tenants-platform-roles', component: () => import('@/pages/settings/PlatformRoleAssignmentPage.vue') },
            ],
          },
          // 基礎設施
          { path: 'brokers', name: 'settings-brokers', component: () => import('@/pages/sys/BrokersAndAccountsPage.vue') },
          {
            path: 'broker-accounts',
            name: 'settings-bacc',
            component: () => import('@/pages/sys/BrokersAndAccountsPage.vue'),
            beforeEnter: (to) => {
              if (to.query.view === 'accounts') return true
              return {
                path: to.path,
                query: { ...to.query, view: 'accounts' },
              }
            },
          },
          { path: 'accounts', name: 'settings-accounts', component: () => import('@/pages/sys/AccountsPage.vue') },
          { path: 'speedy-acc-pass', name: 'settings-speedy', component: () => import('@/pages/join/SpeedyAccPassPage.vue') },
          { path: 'hosts', name: 'settings-hosts', component: () => import('@/pages/sys/HostsPage.vue') },
          { path: 'colo', name: 'settings-colo', component: () => import('@/pages/sys/ColoInstancesPage.vue') },
          // Re-homed from join/ for consolidation under settings hub (migration intent)
          { path: 'upload-cert', name: 'console-join-cert', component: () => import('@/pages/join/UploadCertPage.vue'), meta: { title: 'Upload Certificate' } },
          { path: 'trading-ip', name: 'console-join-trading-ip', component: () => import('@/pages/join/TradingIPPage.vue'), meta: { title: 'Trading IP' } },
          // 商品管理
          {
            path: 'symbols',
            component: () => import('@/pages/product/SymbolManagementHubPage.vue'),
            children: [
              { path: '', redirect: '/settings/symbols/specs' },
              { path: 'specs', name: 'settings-symbols', component: () => import('@/pages/product/ProductSymbolsPage.vue') },
              { path: 'futures', name: 'settings-futures', component: () => import('@/pages/product/FuturesSymbolsPage.vue') },
              { path: 'stocks', name: 'settings-stocks', component: () => import('@/pages/product/StockSymbolsPage.vue') },
              { path: 'options', name: 'settings-options', component: () => import('@/pages/product/OptionSymbolsPage.vue') },
            ],
          },
          { path: 'exchanges', name: 'settings-exchanges', component: () => import('@/pages/product/ExchangesPage.vue') },
          { path: 'asset-classes', name: 'settings-assets', component: () => import('@/pages/product/AssetClassesPage.vue') },
          { path: 'prod-groups', name: 'settings-prodgrp', component: () => import('@/pages/product/ProductGroupsPage.vue') },
          { path: 'calendar', name: 'settings-calendar', component: () => import('@/pages/product/ExchangeCalendarPage.vue') },
          { path: 'real-symbols', name: 'settings-real-symbols', component: () => import('@/pages/product/RealSymbolsPage.vue') },
          { path: 'symbol-events', name: 'settings-symbol-events', component: () => import('@/pages/product/SymbolEventsPage.vue') },
          // 帳務 / 其他
          { path: 'coupons', name: 'settings-coupons', component: () => import('@/pages/accounting/CouponManagementPage.vue') },
          { path: 'level', name: 'settings-level', component: () => import('@/pages/accounting/ManagerLevelPage.vue') },
          { path: 'param', name: 'settings-param', component: () => import('@/pages/accounting/ManagerParamPage.vue') },
        ],
      },
      // Legacy redirects → Settings Hub
      { path: 'sys/system-settings-hub', redirect: '/settings/roles' },
      { path: 'sys/infra-hub', redirect: '/settings/brokers' },
      { path: 'sys/roles', redirect: '/settings/roles' },
      { path: 'sys/user-groups', redirect: '/settings/groups' },
      { path: 'sys/tenants', redirect: '/settings/tenants' },
      { path: 'sys/erp-customers', redirect: '/settings/erp' },
      { path: 'sys/holidays', redirect: '/settings/holidays' },
      { path: 'sys/email-config', redirect: '/settings/email' },
      { path: 'sys/settings', redirect: '/settings/settings' },
      { path: 'sys/brokers', redirect: '/settings/brokers' },
      { path: 'sys/broker-accounts', redirect: '/settings/broker-accounts' },
      { path: 'sys/accounts', redirect: '/settings/accounts' },
      { path: 'sys/hosts', redirect: '/settings/hosts' },
      { path: 'sys/colo-instances', redirect: '/settings/colo' },
      { path: 'product/contract-codes', redirect: '/settings/symbols' },
      { path: 'settings/futures-sym', redirect: '/settings/symbols/futures' },
      { path: 'settings/stocks-sym', redirect: '/settings/symbols/stocks' },
      { path: 'settings/options-sym', redirect: '/settings/symbols/options' },
      { path: 'product/exchanges', redirect: '/settings/exchanges' },
      { path: 'product/asset-classes', redirect: '/settings/asset-classes' },
      { path: 'product/groups', redirect: '/settings/prod-groups' },
      { path: 'product/calendar', redirect: '/settings/calendar' },
      { path: 'product/symbols', redirect: '/settings/symbols' },
      { path: 'product/futures', redirect: '/settings/futures-sym' },
      { path: 'product/stocks', redirect: '/settings/stocks-sym' },
      { path: 'product/options', redirect: '/settings/options-sym' },
      { path: 'accounting/settings', redirect: '/finance/recompute' },
      { path: 'accounting/manager-level', redirect: '/settings/level' },
      { path: 'accounting/manager-param', redirect: '/settings/param' },
      { path: 'accounting/cash', redirect: '/finance/cash' },
      { path: 'accounting/coupons', redirect: '/settings/coupons' },
      { path: 'join/speedy-acc-pass', redirect: '/settings/speedy-acc-pass' },
      { path: 'join/upload-cert', redirect: '/settings/upload-cert' },
      { path: 'join/trading-ip', redirect: '/settings/trading-ip' },
      // NOTE: no `monitoring/market-data` redirect here — `/monitoring/market-data`
      // is a LIVE page (MarketDataViewerPage, see the monitoring hub child above).
      // The old redirect to /settings/symbols shadowed that real route; removed (V2-I8).
      { path: 'crm/long-lived-tokens', redirect: '/settings/tokens' },
      { path: 'crm/svcake-settings', redirect: '/settings/surveycake' },

      // ═══════════════════════════════════════════════════════════════════════
      // Standalone pages (not in Hub)
      // ═══════════════════════════════════════════════════════════════════════

    ],
  },

  // Print page (standalone, no ConsoleLayout)
  {
    path: '/print',
    name: 'print',
    component: () => import('@/pages/performance/PrintPage.vue'),
    meta: {
      title: 'Print Report',
      requiresAuth: true,
      roles: ['site_admin', 'site_owner'],
    },
  },

  // Error routes
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/pages/error/ForbiddenPage.vue'),
    meta: { title: 'Access Denied' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/error/NotFoundPage.vue'),
    meta: { title: 'Page Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

stampSections(routes)
setupRouterGuards(router)

export default router
